/**
 * Admin Onboarding Page
 * TASK-339: Onboarding Wizard UI
 *
 * Multi-step wizard for new customer admin onboarding.
 * Public page - no authentication required.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import KielisLogo from '../../../assets/KielisLogo-no-bg.png';
import { adminOnboardingService } from '../../services/adminOnboardingService';
import { OnboardingState, ONBOARDING_STEPS } from '../../types/adminOnboarding';

// Step components
import PasswordStep from './steps/PasswordStep';
import FirmInfoStep from './steps/FirmInfoStep';
import SecurityStep from './steps/SecurityStep';
import InviteTeamStep from './steps/InviteTeamStep';
import CompletionStep from './steps/CompletionStep';

const AdminOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Onboarding state
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isComplete, setIsComplete] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // TASK-630: backend sends /onboarding for a fresh (zero-client) tenant
  const [redirectUrl, setRedirectUrl] = useState('/dashboard');
  // TASK-631: a require-2fa tenant gets a challenge instead of an access token
  const [challenge, setChallenge] = useState<{ token: string; status: string } | null>(null);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid onboarding link. No token provided.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await adminOnboardingService.validateToken(token);

        if (!response.valid) {
          setError(response.error || 'Invalid or expired onboarding link.');
          setIsLoading(false);
          return;
        }

        setOnboardingState({
          token,
          accountId: response.account_id || '',
          companyName: response.company_name || '',
          adminEmail: response.admin_email || '',
          adminFirstName: response.admin_first_name || '',
          adminLastName: response.admin_last_name || '',
          expiresAt: response.expires_at ? new Date(response.expires_at) : null,
          passwordSet: false,
          firmInfoSaved: false,
          securityConfigured: false,
          teamInvited: false,
        });
        setIsLoading(false);
      } catch (err: any) {
        console.error('Token validation error:', err);
        setError(err?.response?.data?.detail || 'Failed to validate onboarding link.');
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Mark step as complete
  const markStepComplete = (step: number) => {
    setCompletedSteps(prev => new Set([...prev, step]));
  };

  // Navigate to next step
  const goToNextStep = () => {
    markStepComplete(currentStep);
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Navigate to previous step
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Skip optional step
  const skipStep = () => {
    goToNextStep();
  };

  // Complete onboarding
  const handleComplete = async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      const response = await adminOnboardingService.completeOnboarding({ token });

      if (response.success) {
        setAccessToken(response.access_token);
        setRedirectUrl(response.redirect_url || '/dashboard');
        setChallenge(
          response.challenge_token
            ? { token: response.challenge_token, status: response.status || '2fa_required' }
            : null
        );
        setIsComplete(true);
        toast.success('Welcome to Kielis!');
      } else {
        toast.error('Failed to complete onboarding');
      }
    } catch (err: any) {
      console.error('Complete onboarding error:', err);
      toast.error(err?.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle login after completion
  const handleLogin = () => {
    if (accessToken) {
      localStorage.setItem('auth_token', accessToken);
      navigate(redirectUrl, { replace: true });
      return;
    }
    // TASK-631: no access token — tenant enforces 2FA; continue on the challenge page.
    if (challenge) {
      navigate('/login/2fa', {
        replace: true,
        state: {
          challengeToken: challenge.token,
          status: challenge.status,
          email: onboardingState?.adminEmail || '',
          from: redirectUrl,
        },
      });
      return;
    }
    navigate('/login', { replace: true });
  };

  // Loading state
  if (isLoading && !onboardingState) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-kielis-glacier to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-kielis-navy mx-auto mb-4" />
          <p className="text-gray-600">Validating your onboarding link...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-kielis-glacier to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle size={32} className="text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-kielis-navy text-white rounded-lg hover:bg-opacity-90 transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Completion state
  if (isComplete) {
    return (
      <CompletionStep
        companyName={onboardingState?.companyName || ''}
        onLogin={handleLogin}
      />
    );
  }

  if (!onboardingState) return null;

  // Get current step config
  const currentStepConfig = ONBOARDING_STEPS[currentStep - 1];
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === 4;

  return (
    <div className="min-h-screen bg-gradient-to-br from-kielis-glacier to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={KielisLogo} alt="Kielis" className="h-10" />
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{onboardingState.companyName}</p>
            <p className="text-xs text-gray-500">{onboardingState.adminEmail}</p>
          </div>
        </div>
      </header>

      {/* Progress indicator */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            {ONBOARDING_STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                {/* Step circle */}
                <div className="flex flex-col items-center">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                      ${completedSteps.has(step.id)
                        ? 'bg-green-500 text-white'
                        : currentStep === step.id
                          ? 'bg-kielis-navy text-white'
                          : 'bg-gray-200 text-gray-500'
                      }
                    `}
                  >
                    {completedSteps.has(step.id) ? (
                      <CheckCircle size={20} />
                    ) : (
                      step.id
                    )}
                  </div>
                  <span className={`
                    mt-2 text-xs font-medium hidden sm:block
                    ${currentStep === step.id ? 'text-kielis-navy' : 'text-gray-500'}
                  `}>
                    {step.name}
                  </span>
                </div>

                {/* Connector line */}
                {index < ONBOARDING_STEPS.length - 1 && (
                  <div
                    className={`
                      flex-1 h-1 mx-2
                      ${completedSteps.has(step.id) ? 'bg-green-500' : 'bg-gray-200'}
                    `}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="px-6 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Step header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {currentStepConfig.name}
            </h1>
            <p className="text-gray-600">
              {currentStepConfig.description}
            </p>
            {!currentStepConfig.required && (
              <p className="text-sm text-gray-400 mt-1">(Optional)</p>
            )}
          </div>

          {/* Step content */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
            {currentStep === 1 && (
              <PasswordStep
                token={token || ''}
                firstName={onboardingState.adminFirstName}
                lastName={onboardingState.adminLastName}
                onComplete={goToNextStep}
              />
            )}
            {currentStep === 2 && (
              <FirmInfoStep
                token={token || ''}
                companyName={onboardingState.companyName}
                onComplete={goToNextStep}
                onSkip={skipStep}
              />
            )}
            {currentStep === 3 && (
              <SecurityStep
                token={token || ''}
                onComplete={goToNextStep}
                onSkip={skipStep}
              />
            )}
            {currentStep === 4 && (
              <InviteTeamStep
                token={token || ''}
                companyName={onboardingState.companyName}
                onComplete={handleComplete}
                onSkip={handleComplete}
                isLoading={isLoading}
              />
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-6">
            {!isFirstStep ? (
              <button
                onClick={goToPreviousStep}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft size={18} />
                Back
              </button>
            ) : (
              <div />
            )}

            {!isLastStep && currentStep !== 1 && (
              <button
                onClick={skipStep}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                Skip for now
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminOnboardingPage;
